<template>
    <div
        v-qa.hydrated="'hydrated'"
        class="top-bar"
        :data-qa="'top-bar'"
        :data-testid="testId"
    >
        <div class="top-bar-wrapper">
            <div class="wrapper">
                <ButtonRound
                    v-if="!isLogged"
                    :icon="'profile'"
                    aria-label="Account"
                    class="account-button"
                    type="link"
                    size="small"
                    @click="toggleLoginForm"
                />

                <ButtonRound
                    v-else
                    :icon="'profile'"
                    :to="routes.designStudio()"
                    aria-label="Account"
                    class="account-button"
                    type="link"
                    size="small"
                    @click="closeMenu"
                />

                <ThLink
                    v-qa="'cart'"
                    :to="routes.cart()"
                    class="header-button cart-trigger"
                    type="button"
                    rel="nofollow"
                    @click="closeMenu"
                >
                    {{ t('header.cart') }}
                </ThLink>

                <ThLink
                    :to="'/visit-us'"
                    class="header-button visit-us-trigger"
                    type="button"
                    aria-label="Visit us"
                    @click="closeMenu"
                >
                    {{ t('header.visit_us') }}
                </ThLink>

                <WishlistTrigger class="header-button saved-products-trigger" />
            </div>

            <CmsLink
                v-if="
                    topBar &&
                    topBar.consultationPrompt &&
                    topBar.consultationPrompt.link
                "
                :link="topBar.consultationPrompt.link"
                class="book-a-consultation"
            />

            <div
                v-if="!hideSwitchers"
                class="switchers"
            >
                <CurrencySwitcher
                    v-qa="'currency-switcher'"
                    class="currency-switcher"
                />

                <RegionSwitcher
                    v-qa="'region-switcher'"
                    class="region-switcher"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { useMenus } from '@/plugins/menu'
import { useIsOnRoute } from '@/composables/useIsOnRoute'

import {
    ROUTE_PROFILE_DESIGN_STUDIO,
    ROUTE_PROFILE_MY_ORDERS,
    ROUTE_PROFILE_PRODUCTS,
} from '@/router'

import { useRoutes } from '@/router/routes'
import { useAuth } from '@/plugins/auth.ts'

import CurrencySwitcher from '../../app/CurrencySwitcher.vue'
import RegionSwitcher from '../../app/RegionSwitcher.vue'
import CmsLink from '@/components/cms/CmsLink.vue'
import WishlistTrigger from './wishlist/WishlistTrigger.vue'
import ThLink from '@/components/ui/buttons/ThLink.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'

defineProps<{
    testId?: string
}>()

const emit = defineEmits<{
    toggleLoginForm: []
    closeMenu: []
}>()

const routes = useRoutes()

const hideSwitchers = useIsOnRoute([
    ROUTE_PROFILE_DESIGN_STUDIO,
    ROUTE_PROFILE_MY_ORDERS,
    ROUTE_PROFILE_PRODUCTS,
])

const { topBar } = useMenus()
const toggleLoginForm = () => emit('toggleLoginForm')
const closeMenu = () => emit('closeMenu')

const { isLogged } = useAuth()

const { t } = useI18n()
</script>

<style lang="scss" scoped>
.top-bar {
    width: 100%;
    background: $blue;
    color: $white;
    height: 70px;
    z-index: 101;
    position: fixed;
    bottom: 0;
    padding: 8px;
    display: none;

    @include min-width(360) {
        padding: 24px;
    }

    @include min-width(1024) {
        position: relative;
        height: 30px;
        padding: 0;
        display: block;
        bottom: initial;
        background: $stone-2;
        border-bottom: 1px solid $grey-200;
        color: $slate;
    }

    .top-bar-wrapper {
        width: 1440px;
        max-width: 100%;
        margin: 0 auto;
        display: flex;
        justify-content: space-between;

        @include min-width(1024) {
            padding: 0 40px;
        }
    }

    .switchers {
        display: flex;
        align-items: center;

        @include min-width(1024) {
            align-items: initial;
        }

        .region-switcher {
            margin-left: 16px;

            @media (max-width: 359px) {
                margin-left: 0;
            }
        }
    }

    .book-a-consultation {
        font-family: $effra-font-stack;
        font-size: 0.8rem;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: $slate;
        line-height: 30px;
        padding: 0 15px;
        align-content: center;
        margin-right: 15px;
        display: none;

        @include min-width(1024) {
            display: flex;
        }

        &::before {
            display: block;
            font-family: $icon-font;
            content: $icon-location-pin;
            margin-right: 8px;
            font-weight: 600;
        }
    }

    .wrapper {
        display: flex;
        align-items: center;
        gap: 4px;

        @include min-width(1024) {
            display: none;
        }
    }

    .account-button {
        display: block;

        :deep(.icon) {
            color: $white;
            font-weight: 600;
        }
    }

    .header-button {
        @include visually-hidden-text;

        background: transparent;
        appearance: none;
        border: none;
        width: 40px;
        height: 40px;
        display: block;
        cursor: pointer;
        text-align: center;
        line-height: 2.5rem;
        color: $white;

        &::before {
            font-family: $icon-font;
            display: block;
            text-indent: initial;
            font-size: 1.125rem;
        }

        &.visit-us-trigger {
            display: block;

            @include min-width(410) {
                display: none;
            }

            &::before {
                font-size: 1.2rem;
                font-weight: 600;
                content: $icon-location-pin;
            }
        }

        &.saved-products-trigger {
            &::before {
                color: $white;
                font-size: 1.2rem;
                content: $icon-heart;
            }
        }

        &.cart-trigger {
            display: block;

            @include min-width(410) {
                display: none;
            }

            &::before {
                font-size: 1.25rem;
                font-weight: 600;
                content: $icon-bag;
            }
        }
    }
}
</style>
